import { HfInference } from '@huggingface/inference';
import axios from 'axios';

// In your utils.js or wherever your API calls are defined
// const API_URL = process.env.REACT_APP_API_URL || 'https://x-summary-server.vercel.app';
const API_URL = process.env.REACT_APP_API_URL || 'http://localhost:4000';

export async function fetchTweetContent(tweetUrl) {
  try {
    // Add includeThread parameter to get the full thread
    const response = await fetch(`${API_URL}/api/fetch-tweet?url=${encodeURIComponent(tweetUrl)}&includeThread=true`);
    if (!response.ok) {
      throw new Error('Failed to fetch tweet content');
    }
    const data = await response.json();
    return data;
  } catch (error) {
    console.error('Error fetching tweet:', error);
    throw error;
  }
}

export async function generateMemeText(text) {
  const preprocessedText = preprocessThreadText(text);

  const prompt = `
Turn this text into one HILARIOUS meme caption that exaggerates its specific details:

"${preprocessedText}"

MOST IMPORTANT:
- One complete sentence, max 40 words, punchy and ridiculous

ABSOLUTE REQUIREMENTS:
- Make it ABSURDLY funny, tied directly to the tweet’s content (no generic jokes)
- Use over-the-top humor or wild exaggeration based on the exact details provided
- No mean or offensive vibes, just chaotic fun that anyone would get
- Must stand alone and make people laugh without extra context
- Examples using specific details:
  - "Built a billion-dollar empire from £500? Bro’s out here flexing pizza dough into gold!"
  - "Pizza guy turned billionaire with Gymshark, now I’m broke ordering pepperoni in his honor!"
  - "Industry giants stunned by a £500 plan so simple my dog could’ve barked it out!"

RETURN ONLY THE MEME CAPTION - NO EXPLANATION, NO SYMBOLS, NO EXTRA TEXT.
`;

  try {
    const response = await hf.textGeneration({
      model: 'mistralai/Mistral-7B-Instruct-v0.2',
      inputs: prompt,
      parameters: {
        max_new_tokens: 80, // Room for ~25 words
        temperature: 1.0,   // High creativity for absurdity
        return_full_text: false,
      },
    });

    let caption = response.generated_text.trim();
    // Clean up unwanted chars or formatting
    caption = caption.replace(/[^\w\s.,!?'-]/g, ''); // Allow basic punctuation

    return caption;
  } catch (error) {
    console.error("Error generating meme text:", error);
    // Fallback tied to business/pizza theme if tweet isn’t available
    return "Pizza boy built a billion-dollar gym empire while I’m still tipping for extra cheese!";
  }
}

// Update your extractKeywordWithAI function to focus on humor
export async function extractKeywordWithAI(text) {
  try {
    const prompt = `
For this text, identify what type of FUNNY REACTION IMAGE would best match:

"${text}"

Choose one reaction type from this list that would be FUNNIEST:
1. Confused reaction - for puzzling or illogical statements
2. Shocked reaction - for surprising or outrageous content
3. Mind blown reaction - for amazing revelations or wild ideas
4. Facepalm reaction - for obviously bad ideas or mistakes
5. Laughing reaction - for genuinely funny situations
6. Eyeroll reaction - for annoying or ridiculous scenarios
7. Excited reaction - for over-the-top enthusiasm
8. Suspicious reaction - for sketchy or dubious claims

Return ONLY the reaction type number and name, like "3. Mind blown reaction"
`;

    const response = await hf.textGeneration({
      model: 'mistralai/Mistral-7B-Instruct-v0.2',
      inputs: prompt,
      parameters: {
        max_new_tokens: 20,
        temperature: 0.5,
        return_full_text: false,
      },
    });

    // Parse the response to extract just the reaction type
    const responseText = response.generated_text.trim();
    
    // Extract the reaction type using regex
    const reactionMatch = responseText.match(/\d+\.\s*([\w\s]+)(?:reaction)?/i);
    const reactionType = reactionMatch ? reactionMatch[1].trim().toLowerCase() : "confused";
    
    console.log(`AI selected reaction type: "${reactionType}" for text: "${text}"`);
    
    return reactionType;
  } catch (error) {
    console.error("Error extracting reaction type with AI:", error);
    return "confused"; // Fallback
  }
}

// Updated generateMemeImage to use curated funny reaction searches
export async function generateMemeImage(summary) {
  const GIPHY_API_KEY = process.env.REACT_APP_GIPHY_API_KEY;

  if (!GIPHY_API_KEY) {
    console.error("Giphy API key is missing!");
    return "https://media.giphy.com/media/26FPy3QZQqGtD5pVC/giphy.gif"; // Success Kid as a last resort
  }

  try {
    // Extract keywords from the summary for relevance
    const keywords = summary
      .toLowerCase()
      .split(" ")
      .filter(word => word.length > 3 && !["this", "that", "with", "from"].includes(word))
      .slice(0, 3) // Take up to 3 key words
      .join(" ");

    // Build a dynamic search term with "funny meme" + keywords
    const searchTerm = keywords.length > 0 ? `funny meme ${keywords}` : "funny meme trending";

    console.log(`Giphy search term: "${searchTerm}"`);

    // Search Giphy with the dynamic term
    const response = await axios.get(
      `https://api.giphy.com/v1/gifs/search?api_key=${GIPHY_API_KEY}&q=${encodeURIComponent(searchTerm)}&limit=25&rating=pg&lang=en`
    );

    const gifs = response.data.data;
    if (!gifs || gifs.length === 0) throw new Error("No GIFs found for search term");

    // Filter for quality (decent size, reasonable aspect ratio)
    const qualityGifs = gifs.filter(gif => {
      const width = parseInt(gif.images.original.width);
      const height = parseInt(gif.images.original.height);
      return width >= 200 && height >= 200 && width / height < 2.5 && height / width < 2.5;
    });

    if (qualityGifs.length === 0) throw new Error("No quality GIFs found");

    // Randomly pick from the top 10 results for variety (top results are more popular)
    const randomIndex = Math.floor(Math.random() * Math.min(10, qualityGifs.length));
    const selectedGif = qualityGifs[randomIndex];

    console.log(`Selected GIF: "${selectedGif.title}" (URL: ${selectedGif.images.fixed_height_still.url})`);
    return selectedGif.images.fixed_height_still.url; // Use still image for meme card

  } catch (error) {
    console.error("Error fetching meme image:", error);
    try {
      // Fallback to Giphy's trending GIFs for fresh, funny variety
      const trendingResponse = await axios.get(
        `https://api.giphy.com/v1/gifs/trending?api_key=${GIPHY_API_KEY}&limit=25&rating=pg`
      );
      const trendingGifs = trendingResponse.data.data;
      const qualityTrending = trendingGifs.filter(gif => {
        const width = parseInt(gif.images.original.width);
        const height = parseInt(gif.images.original.height);
        return width >= 200 && height >= 200 && width / height < 2.5 && height / width < 2.5;
      });

      if (qualityTrending.length === 0) return "https://media.giphy.com/media/l0MYt5jPRARFEfkY8/giphy.gif"; // Drake Yes as final fallback

      const randomTrendingIndex = Math.floor(Math.random() * Math.min(10, qualityTrending.length));
      const selectedTrending = qualityTrending[randomTrendingIndex];
      console.log(`Fallback to trending GIF: "${selectedTrending.title}"`);
      return selectedTrending.images.fixed_height_still.url;
    } catch (trendingError) {
      console.error("Error fetching trending GIFs:", trendingError);
      return "https://media.giphy.com/media/l0MYt5jPRARFEfkY8/giphy.gif"; // Drake Yes
    }
  }
}

const hf = new HfInference('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');

// Function to preprocess the thread text before sending to the AI
function preprocessThreadText(text) {
  // Check if text contains markers indicating a list or steps
  const hasListIndication = /(\d+[\s]*steps|\d+[\s]*ways|steps to|how to|tips for)/i.test(text);
  
  // If text seems to contain a list but doesn't show many numbered items, 
  // it might be only giving an introduction without the actual list
  if (hasListIndication) {
    // Count how many numbered points we can find
    const numberedPoints = text.match(/\n\d+[\.\)]/g);
    const numberedPointsCount = numberedPoints ? numberedPoints.length : 0;
    
    // If we mention steps but don't see many numbered points, prioritize finding them
    if (numberedPointsCount < 3) {
      console.log("Detected list indication but insufficient numbered points");
    }
  }
  
  // Replace '---' thread separators with a more natural paragraph break
  // This helps the model treat the thread as a cohesive document
  let preprocessed = text.replace(/\n\s*---\s*\n/g, "\n\n");
  
  // Return the preprocessed text
  return preprocessed;
}

export async function summarizeText(text) {
  // Preprocess the thread text to improve summarization
  const preprocessedText = preprocessThreadText(text);
  
  // If text is very long, we need a different approach to ensure the model
  // captures the entire content, especially any lists or steps mentioned
  const isLong = preprocessedText.length > 1500;
  
  // Create a more targeted prompt for long threads
  const prompt = isLong 
    ? `
Summarize this text in exactly 3 bullet points. Be specific and include key details:

"${preprocessedText}"

VERY IMPORTANT:
- If the text mentions any numbered steps, tips, or ways to do something, INCLUDE THESE KEY POINTS
- Focus on the SPECIFIC ACTIONS or RECOMMENDATIONS mentioned
- Do not say "X steps can be taken" - actually list what those steps or tips are
- Each bullet point should be concrete and specific
- Bullets must contain actual information from the text, not meta-references
- Do not use phrases like "point 1" or similar

Return ONLY 3 concise, specific bullet points with the most important information.
`
    : `
Create exactly 3 bullet points that summarize this text:

"${preprocessedText}"

STRICT RULES:
- NEVER use "user", "author", "person", "they", or any third-person references
- Write each point as a direct statement - NOT as "someone said/did X"
- No numbering, no special formatting
- Focus on the key points or opinions directly
- Keep each point brief and clear
- Do not refer to "this tweet" or "this post"
- Do not start with "I" or use first-person perspective
- NEVER include phrases like "point 1", "points", "bullet point", or similar meta-references
- If the text mentions any list of tips or steps, INCLUDE THOSE SPECIFIC STEPS
- Do not say "X steps can be taken" without explaining what those actual steps are
- Each bullet should contain actual information from the text, not formatting instructions

Return ONLY 3 complete bullet points with NO references to who wrote the original text.
`;

  try {
    // For longer texts, we might need to increase the token limit
    const maxTokens = isLong ? 250 : 200;
    
    const response = await hf.textGeneration({
      model: 'mistralai/Mistral-7B-Instruct-v0.2',
      inputs: prompt,
      parameters: {
        max_new_tokens: maxTokens,
        temperature: 0.7,
        return_full_text: false,
      },
    });
    
    console.log("AI Raw Response:", response.generated_text);
    
    // Process the response to extract clean bullet points
    let points = response.generated_text
      .split('\n')
      .map(line => line.trim())
      .filter(line => line.length > 0)
      // Remove any bullet point characters or numbering
      .map(line => line.replace(/^[-•*+\d\.\)\]\s]+/, ''))
      // Remove any markdown characters
      .map(line => line.replace(/[*_~`]/g, ''))
      // Remove phrases like "Point 1:", "First:", etc.
      .map(line => line.replace(/^(point|bullet|first|second|third|1st|2nd|3rd)[\s\:]+/i, ''))
      // Filter out any lines that look like instructions or meta-references
      .filter(line => !line.match(/^(bullet points|example|good|bad|summary|rule|note|points?(\s+\d+)?:?$)/i))
      .slice(0, 3); // Ensure we have at most 3 points
    
    // Aggressive removal of problematic terms
    const problematicTerms = [
      'user', 'author', 'writer', 'tweet', 'they', 'their', 'them', 'person', 
      'individual', 'poster', 'tweeter', 'original poster', 'op'
    ];
    
    // Process each point to remove problematic references
    points = points.map(point => {
      let processed = point;
      
      // Replace all third-person references
      problematicTerms.forEach(term => {
        // Handle the term with word boundaries and optional possessive
        const regex = new RegExp(`\\b${term}(?:'s)?\\b`, 'gi');
        processed = processed.replace(regex, '');
        
        // Also handle "the [term]" phrases
        const theRegex = new RegExp(`\\b(the|this|that)\\s+${term}(?:'s)?\\b`, 'gi');
        processed = processed.replace(theRegex, '');
      });
      
      // Replace common verb phrases associated with third-person references
      processed = processed.replace(/\b(user|author|writer|poster|they|person)\s+(is|was|has|had|does|did|feels|felt|thinks|thought|believes|believed|finds|found|seems|seemed)\b/gi, '');
      
      // Clean up any awkward spacing left from removals
      processed = processed.replace(/\s{2,}/g, ' ').trim();
      
      // Check if the point is just saying "steps can be taken" without specifics
      if (/steps can be taken|ways to|methods to/i.test(processed) && processed.length < 80) {
        // This is likely a non-specific point that doesn't actually list the steps
        // Mark it for possible replacement
        console.log("Detected generic 'steps' reference without specifics:", processed);
        processed = "GENERIC_STEPS_REFERENCE: " + processed;
      }
      
      // Capitalize first letter after removing terms if needed
      if (processed.length > 0) {
        processed = processed.charAt(0).toUpperCase() + processed.slice(1);
      }
      
      return processed;
    });
    
    // Additional filter to remove points that are likely formatting instructions or meta-references
    points = points.filter(point => {
      // Check if the point is just saying "point X" or similar
      const isMetaPoint = /^(point|bullet|keypoint|key\s+point|main\s+point)s?(\s+\d+)?:?$/i.test(point);
      
      // Check if the point is too short (likely not actual content)
      const isTooShort = point.length < 10;
      
      // Check if the point contains only generic meta-references
      const isGenericMetaRef = /^(points?|bullets?|summary)\s*(of|about|from|in)?\s*(the|this)?\s*(post|text|content)?$/i.test(point);
      
      // Check if it's a generic "steps can be taken" point that we marked
      const isGenericSteps = point.startsWith("GENERIC_STEPS_REFERENCE:");
      
      return !isMetaPoint && !isTooShort && !isGenericMetaRef && !isGenericSteps;
    });
    
    // Filter out any empty points after processing
    points = points.filter(point => point.length > 0);
    
    // If we don't have enough points, try to extract key steps from the original text
    if (points.length < 3 && isLong) {
      // Extract numbered points from the original text
      const numberedPointPattern = /\d+\.\s+([^\n]+)/g;
      let match;
      const extractedPoints = [];
      
      while ((match = numberedPointPattern.exec(preprocessedText)) !== null && extractedPoints.length < 3) {
        // Extract and clean the point
        let extractedPoint = match[1].trim();
        
        // Make sure it's substantial enough
        if (extractedPoint.length > 15) {
          extractedPoints.push(extractedPoint);
        }
      }
      
      // Add any extracted points to our results
      extractedPoints.forEach(point => {
        if (points.length < 3) {
          points.push(point);
        }
      });
    }
    
    // If we STILL don't have exactly 3 bullet points, add neutral fallbacks
    const genericPoints = [
      "Key points highlighted and explained clearly",
      "Important details provided with supporting context",
      "Valuable insights shared regarding the topic"
    ];
    
    while (points.length < 3) {
      points.push(genericPoints[points.length % genericPoints.length]);
    }
    
    // Make sure we only have 3 points (in case we somehow got more)
    points = points.slice(0, 3);
    
    // Format with bullet points
    const formattedPoints = points.map(point => `• ${point}`);
    
    return formattedPoints.join('\n');
  } catch (error) {
    console.error("Error generating summary:", error);
    // Fallback summary with no references to users
    return [
      "• Key information presented in a clear manner",
      "• Important details highlighted with context",
      "• Valuable insights provided on the topic"
    ].join('\n');
  }
}

// Add this to your utils.js file

export async function generateTrollSummary(text) {
  // Preprocess the thread text first
  const preprocessedText = preprocessThreadText(text);
  
  // Create a prompt specifically designed for generating humorous/troll summaries
  const prompt = `
Create exactly 3 funny, satirical bullet points that exaggerate and poke fun at this post:

"${preprocessedText}"

RULES:
- Be humorous but not mean-spirited or harmful
- Exaggerate the points in a funny way - think "they said X but what they REALLY meant was Y"
- Use hyperbole and satire
- Keep each point concise, witty, and unexpected
- No references to "the user" or "the author" - write as direct statements
- Don't use phrases like "bullet point" or "point 1"
- Format with simple bullet points only
- Be slightly irreverent but never cruel or offensive

Return ONLY 3 funny bullet points that would make someone laugh.
`;

  try {
    const response = await hf.textGeneration({
      model: 'mistralai/Mistral-7B-Instruct-v0.2',
      inputs: prompt,
      parameters: {
        max_new_tokens: 250,
        temperature: 0.9, // Higher temperature for more creative responses
        return_full_text: false,
      },
    });
    
    console.log("Troll Response:", response.generated_text);
    
    // Process the response to extract clean bullet points
    let points = response.generated_text
      .split('\n')
      .map(line => line.trim())
      .filter(line => line.length > 0)
      // Remove any bullet point characters or numbering
      .map(line => line.replace(/^[-•*+\d\.\)\]\s]+/, ''))
      // Remove any markdown characters
      .map(line => line.replace(/[*_~`]/g, ''))
      // Remove phrases like "Point 1:", "First:", etc.
      .map(line => line.replace(/^(point|bullet|first|second|third|1st|2nd|3rd)[\s\:]+/i, ''))
      .slice(0, 3); // Ensure we have at most 3 points
    
    // Filter out any empty points after processing
    points = points.filter(point => point.length > 0);
    
    // If we don't have exactly 3 bullet points, add fallback funny points
    const funnyFallbacks = [
      "Apparently this post was written while riding a unicycle and juggling flaming torches",
      "Breaking news: Captain Obvious has discovered water is wet and the sky is blue",
      "This tweet contains more hot air than a balloon festival in summer",
      "Congratulations on saying absolutely nothing with the maximum number of characters",
      "If eye-rolling burned calories, reading this would make you lose 5 pounds"
    ];
    
    while (points.length < 3) {
      points.push(funnyFallbacks[Math.floor(Math.random() * funnyFallbacks.length)]);
    }
    
    // Make sure we only have 3 points
    points = points.slice(0, 3);
    
    // Format with bullet points and add funny emoji
    const emojis = ['😂', '🙄', '💀', '🤦‍♂️', '🤡', '👽', '🧢'];
    const formattedPoints = points.map(point => {
      const randomEmoji = emojis[Math.floor(Math.random() * emojis.length)];
      return `• ${point} ${randomEmoji}`;
    });
    
    return formattedPoints.join('\n');
  } catch (error) {
    console.error("Error generating troll summary:", error);
    // Fallback funny summary
    return [
      "• Was this tweet written by AI or just someone trying really hard to sound important? 🤔",
      "• If I had a dollar for every actual insight in this thread, I'd be exactly $0 richer 💸",
      "• The most impressive thing about this post is how it says so little with so many words 🏆"
    ].join('\n');
  }
}

export async function generateThreadFromText(text, isRegeneration = false, threadStyle = 'standard', tempAdjustment = 0) {
  try {
    // More aggressive cleaning of the input text
    const cleanText = text
      .replace(/\[uid:[^[\]]*\]/, '')
      .replace(/\[[^\]]*\]/g, '')  // Remove ALL square brackets and their contents
      .trim();
    
    // Use high temperature for creativity
    let temperature = 0.9 + (tempAdjustment || 0);
    if (temperature > 0.98) temperature = 0.98;
    
    // Generate unique request ID to prevent caching
    const requestId = Date.now() + Math.floor(Math.random() * 1000000);
    
    // Style definitions with better examples
    const styleDefinitions = {
      standard: {
        name: "Standard",
        instruction: "Use a clear, engaging style with natural flow and compelling narrative",
        tone: "Professional but conversational, with a focus on clarity and engagement",
      },
      exciting: {
        name: "Exciting",
        instruction: "Use high-energy language with strategic exclamations and emojis",
        tone: "Highly enthusiastic, energetic, and optimistic with a focus on impact",
      },
      detailed: {
        name: "Detailed",
        instruction: "Include specific facts, numbers, and detailed explanations",
        tone: "Analytical, thorough, and informative with a focus on depth",
      },
      journalist: {
        name: "Journalist",
        instruction: "Present information factually and objectively like a news report",
        tone: "Clear, direct, and fact-focused with a professional news style",
      },
      troll: {
        name: "Troll",
        instruction: "Use humorous exaggeration and playful sarcasm (not mean-spirited)",
        tone: "Irreverent, witty, and over-the-top without being offensive",
      },
      cryptobro: {
        name: "Crypto Bro",
        instruction: "Use crypto slang, excessive optimism, and HODL mentality",
        tone: "Ultra bullish, using diamond hands/paper hands metaphors, calling things mooning/pumping",
      }
    };
    
    // Get the selected style or default to standard
    const selectedStyle = styleDefinitions[threadStyle] || styleDefinitions.standard;
    
    // Add style-specific temperature adjustment
    if (threadStyle === 'exciting' || threadStyle === 'troll') {
      temperature += 0.05;
    } else if (threadStyle === 'cryptobro') {
      temperature += 0.07;
    } else if (threadStyle === 'detailed' || threadStyle === 'journalist') {
      temperature -= 0.05;
    }
    
    // Build an enhanced prompt with the selected style
    const prompt = `
Create a Twitter/X thread with EXACTLY 4-5 posts based on this text:

"${cleanText}"

STYLE GUIDE:
- Use the "${selectedStyle.name}" style
- Tone: ${selectedStyle.tone}
- Style instruction: ${selectedStyle.instruction}

RULES:
- Create EXACTLY 4-5 posts with distinct engaging points
- Each post must be complete and under 280 characters
- Be highly creative and engaging
- Posts should flow naturally from one to the next
- Add relevant hashtags to the final post only

IMPORTANT: ONLY provide the actual posts content and nothing else. DO NOT include ANY instructions, reasoning, or descriptive text. 
DO NOT start with things like "First post:" or "Introduce the topic".
DO NOT include any code blocks, formatting markers, or explanations.

Return your response as plain text with posts numbered like:
1. [Just the actual post content]
2. [Just the actual post content]
3. [Just the actual post content]
4. [Just the actual post content]

REQUEST ID: ${requestId}
`;

    const hf = new HfInference('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');
    
    // Make the API call with unique parameters to prevent caching
    const response = await hf.textGeneration({
      model: 'mistralai/Mistral-7B-Instruct-v0.2',
      inputs: prompt,
      parameters: {
        max_new_tokens: 1000,
        temperature: temperature,
        top_p: 0.95,
        top_k: 80,
        repetition_penalty: 1.15,
        seed: requestId,
        return_full_text: false,
      },
    });
    
    // Aggressively clean the response of any code block markers or other formatting
    const cleanedResponse = response.generated_text
      .replace(/```[\w]*\n?/g, '') // Remove code block markers (```python, ```js, etc.)
      .replace(/```/g, '')         // Remove remaining backticks
      .replace(/`/g, '')           // Remove single backticks
      .trim();
    
    // Process the response to extract thread posts
    let threadPosts = [];
    
    // Try to extract numbered posts (like "1. Post content")
    const numberedPostPattern = /\d+\.?\s+([^\n]+(?:\n(?!\d+\.)[^\n]+)*)/g;
    let match;
    while ((match = numberedPostPattern.exec(cleanedResponse)) !== null) {
      if (match[1] && match[1].trim().length > 0) {
        threadPosts.push(match[1].trim());
      }
    }
    
    // If numbered post extraction didn't work well, try other methods
    if (threadPosts.length < 2) {
      // Try to split by newlines
      threadPosts = cleanedResponse
        .split(/\n\n+|\r\n\r\n+/)
        .map(post => post.trim())
        .filter(post => post.length > 0 && post.length < 500);
      
      // Remove numbering if it exists
      threadPosts = threadPosts.map(post => post.replace(/^\d+\.?\s+/, ''));
    }
    
    // If we still don't have enough posts, try other separators
    if (threadPosts.length < 2) {
      // Try splitting by other common separators
      threadPosts = cleanedResponse
        .split(/\n(?=\d+[\.\)]|\*|\-|\•)/)
        .map(post => post.trim())
        .filter(post => post.length > 0 && post.length < 500);
      
      // Remove numbering and bullet points
      threadPosts = threadPosts.map(post => post.replace(/^(\d+[\.\)]|\*|\-|\•)\s*/, ''));
    }
    
    // Clean up the posts - very aggressive cleanup
    threadPosts = threadPosts
      .map(post => {
        // Start with a clean post
        let cleaned = post.trim();
        
        // Remove any code block markers
        cleaned = cleaned.replace(/```[\w]*\n?/g, '');
        cleaned = cleaned.replace(/```/g, '');
        cleaned = cleaned.replace(/`/g, '');
        
        // Remove quotes and brackets
        cleaned = cleaned.replace(/^["'\[\]]+/, '');  // At beginning
        cleaned = cleaned.replace(/["'\[\]]+$/, '');  // At end
        cleaned = cleaned.replace(/\[.*?\]/g, '');    // Bracket content anywhere
        
        // Fix escaped characters
        cleaned = cleaned.replace(/\\n/g, '\n');
        cleaned = cleaned.replace(/\\"/g, '"');
        cleaned = cleaned.replace(/\\'/g, "'");
        
        // Remove JSON formatting artifacts
        cleaned = cleaned.replace(/,\s*$/, '');  // Trailing commas
        
        // CRITICAL: Remove reasoning or instruction leaks
        // Remove common instruction artifacts that might appear at the beginning
        cleaned = cleaned.replace(/^(First|Start|Begin|Open|Introduce|Present|Describe|Explain|Start by|First,|Begin by|Opening with|Let's|Should|We|I will|Here|This)/i, '');
        
        // Remove any text in square brackets like "[Introduction]"
        cleaned = cleaned.replace(/\[([^\]]+)\]/, '');
        
        // Remove common instruction patterns with colons
        cleaned = cleaned.replace(/^[^:]+:\s*/i, '');
        
        // Remove any text that describes what the post is doing rather than being the post
        const descriptionPatterns = [
          /^(This post (should|will|would) )/i,
          /^(In this post[,:]? )/i,
          /^(For this post[,:]? )/i,
          /^(The first post (should|will|would) )/i,
          /^(Post \d+[,:]? )/i,
          /^(Tweet \d+[,:]? )/i,
          /^(Main point[,:]? )/i,
          /^(Key point[,:]? )/i,
          /^(Focus on )/i,
          /^(Highlight )/i,
          /^(Emphasize )/i,
          /^(Mention )/i,
          /^(Talk about )/i,
          /^(Discuss )/i
        ];
        
        // Apply all the description patterns
        descriptionPatterns.forEach(pattern => {
          cleaned = cleaned.replace(pattern, '');
        });
        
        // Fix capitalization after removing prefixes
        if (cleaned.length > 0 && /[a-z]/.test(cleaned[0])) {
          cleaned = cleaned[0].toUpperCase() + cleaned.substring(1);
        }
        
        return cleaned.trim();
      })
      .filter(post => post && post.length > 0);
    
    // Make sure we have at least 4 posts
    if (threadPosts.length < 4) {
      console.log("Not enough posts, splitting existing ones");
      
      // Sort posts by length to get the longest ones first
      const sortedPosts = [...threadPosts].sort((a, b) => b.length - a.length);
      
      while (threadPosts.length < 4 && sortedPosts.length > 0) {
        // Get the longest remaining post
        const longest = sortedPosts.shift();
        
        if (longest && longest.length > 100) {
          // Find good split points
          const midpoint = Math.floor(longest.length / 2);
          let splitPoint = longest.lastIndexOf('. ', midpoint + 15);
          
          // If no good sentence break, try other punctuation
          if (splitPoint === -1 || splitPoint < longest.length / 4) {
            splitPoint = longest.lastIndexOf('! ', midpoint);
          }
          if (splitPoint === -1 || splitPoint < longest.length / 4) {
            splitPoint = longest.lastIndexOf('? ', midpoint);
          }
          if (splitPoint === -1 || splitPoint < longest.length / 4) {
            splitPoint = longest.lastIndexOf(', ', midpoint);
          }
          if (splitPoint === -1 || splitPoint < longest.length / 4) {
            splitPoint = longest.lastIndexOf(' ', midpoint);
          }
          
          if (splitPoint > 0) {
            const part1 = longest.substring(0, splitPoint + 1).trim();
            const part2 = longest.substring(splitPoint + 1).trim();
            
            // Replace the original post with the first part
            const index = threadPosts.indexOf(longest);
            if (index !== -1) {
              threadPosts[index] = part1;
              threadPosts.splice(index + 1, 0, part2);
            }
          }
        }
      }
      
      // If we still don't have enough posts, create additional ones by breaking up the text
      if (threadPosts.length < 4 && cleanText.length > 50) {
        const sentences = cleanText.match(/[^.!?]+[.!?]+/g) || [cleanText];
        
        let currentPost = "";
        const newPosts = [];
        
        for (const sentence of sentences) {
          if (currentPost.length + sentence.length > 250) {
            if (currentPost) newPosts.push(currentPost.trim());
            currentPost = sentence.trim();
          } else {
            currentPost += " " + sentence.trim();
          }
        }
        
        if (currentPost) newPosts.push(currentPost.trim());
        
        // Add any new posts we created if needed
        while (threadPosts.length < 4 && newPosts.length > 0) {
          threadPosts.push(newPosts.shift());
        }
      }
    }
    
    // Limit to 5 posts maximum
    if (threadPosts.length > 5) {
      threadPosts = threadPosts.slice(0, 5);
    }
    
    // Ensure all posts fit Twitter's character limit
    threadPosts = threadPosts.map(post => {
      if (post.length > 280) {
        const cutPoint = post.lastIndexOf(' ', 277);
        return post.substring(0, cutPoint > 200 ? cutPoint : 277) + '...';
      }
      return post;
    });
    
    // Final pass to remove any lingering code block markers that might have snuck through
    threadPosts = threadPosts.map(post => {
      return post
        .replace(/```[\w]*\n?/g, '')
        .replace(/```/g, '')
        .replace(/`/g, '')
        .trim();
    });
    
    // Add hashtags to the final post if needed
    if (threadPosts.length > 0 && !threadPosts[threadPosts.length - 1].includes('#')) {
      // Extract words from input to create relevant hashtags
      const words = cleanText
        .toLowerCase()
        .split(/\s+/)
        .filter(word => word.length > 4)
        .map(word => word.replace(/[^a-z0-9]/g, ''))
        .filter(word => word.length > 4);
      
      // Get 1-2 most relevant words from the input
      const relevantWords = [...new Set(words)].slice(0, 2);
      
      // Add style-specific hashtags
      const styleHashtags = {
        standard: ['ContentCreation'],
        exciting: ['MustTry'],
        detailed: ['Tips'],
        journalist: ['Update'],
        troll: ['JustSaying'],
        cryptobro: ['WAGMI']
      };
      
      // Combine relevant words with style hashtags
      const hashtagWords = [...relevantWords, ...(styleHashtags[threadStyle] || [])];
      const hashtags = hashtagWords.map(word => `#${word}`).join(' ');
      
      // Add hashtags if they fit
      const finalPost = threadPosts[threadPosts.length - 1];
      if (finalPost.length + hashtags.length + 2 <= 280) {
        threadPosts[threadPosts.length - 1] = `${finalPost}\n\n${hashtags}`;
      } else if (threadPosts.length < 5) {
        threadPosts.push(hashtags);
      }
    }
    
    return threadPosts;
  } catch (error) {
    console.error("Error generating thread:", error);
    
    // Generic fallback that doesn't assume any specific topic
    return [
      "Here's the first part of this thread about the topic.",
      "Let me continue with some additional insights and information.",
      "Building on those points, here are some further considerations worth noting.",
      "To conclude, these are the key takeaways from this discussion."
    ];
  }
}